import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../app/StyleGuide';
import { PrimaryButton, RoundedIconButton } from '../../components/Buttons';
import { Input } from '../../components/FormElements';
import alertService from '../../services/AlertService';
import windowService from '../../services/windowService';
import { useUser } from '../authentication';
import { joinOrViaMembershipCode } from './api/joinOrViaMembershipCode';
import ErrorMessage from '../../components/ErrorMessage';
import WhiteLabel from '../../filters/WhiteLabel';
import { handleError } from '../../utils/apiUtils';

const getOriginBranding = () => {
  return {
    logoSrc: WhiteLabel.logoSrc,
    alt: WhiteLabel.name,
  };
};

const RedeemInviteContainer = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: flex-start;
  padding-top: ${Grid._10};
  justify-content: center;
  background-color: ${Color.Gray._10};

  @media (min-width: ${Breakpoints.screen_xs}) {
    align-items: center;
    padding-top: 0;
  }
`;

const Logo = styled.img`
  width: 225px;
`;

const RedeemInviteFormContainer = styled.div`
  max-width: 375px;
  width: 100%;
  text-align: center;
  margin: 0 ${Grid._4};

  h2 {
    margin: 0;
  }

  p {
    color: ${Type.Color.medium};
    margin: 0;
  }
`;

const HeroFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Grid._4};
`;

const Form = styled.form`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  width: 100%;
`;

const EasyLinkInput = styled(Input)`
  width: 100%;
  text-transform: uppercase;
  text-align: center;
  &.invalid {
    border-color: ${Color.Red._50};
  }
`;

const RedeemInviteErrorMessage = styled(ErrorMessage)`
  margin: 0;
`;

const CloseButton = styled(RoundedIconButton)`
  position: absolute;
  top: ${Grid._4};
  right: ${Grid._5};
`;

type Params = {
  prefix?: string;
  suffix?: string;
};

function parseCode(input: string): [string, string] {
  if (input) {
    const code = input.trim();
    if (code.length === 8) {
      return [code.slice(0, 4).toUpperCase(), code.slice(-4).toUpperCase()];
    }
    if (code.length === 9 && (code.split('-').length === 2 || code.split(' ').length === 2)) {
      return [code.slice(0, 4).toUpperCase(), code.slice(-4).toUpperCase()];
    }
  }
  return ['', ''];
}

export function RedeemInvite() {
  const { user } = useUser();
  const { prefix: routePrefix, suffix: routeSuffix } = useParams<Params>();

  const [codeInput, setCodeInput] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [preventDoubleRedeem, setPreventDoubleRedeem] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (routePrefix && routeSuffix) {
      setCodeInput(`${routePrefix}-${routeSuffix}`);
      setPreventDoubleRedeem(true);
      setTimeout(() => {
        redeem(`${routePrefix}-${routeSuffix}`);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePrefix, routeSuffix]);

  const redeem = (code: string) => {
    const valid = code && code.replace(/\s|-/g, '').length >= 8;
    setSubmitted(true);
    if (!valid || preventDoubleRedeem) return;

    setPreventDoubleRedeem(true);

    const [prefix, suffix] = parseCode(code);

    if (!prefix) {
      setErrorMessage("We couldn't recognize the Easy Link.\nPlease try again and check for any mistyped characters.");
      setPreventDoubleRedeem(false);
      setError(true);
      return;
    }

    if (suffix && suffix.length === 4 && user?.userId && user?.logInEmail && user?.firstName && user?.lastName) {
      joinOrViaMembershipCode(user.userId, user.logInEmail, user.firstName, user.lastName, prefix, suffix)
        .then(() => {
          alertService.showOnNextPage('Easy Link Accepted');
          setPreventDoubleRedeem(false);
          windowService.redirectTo('/dashboard');
        })
        .catch((err: { code: string; name: string; message: string }) => {
          setPreventDoubleRedeem(false);
          if (err.code === 'MembershipCodeAlreadyRedeemedForUser') {
            setError(true);
            setErrorMessage(
              "You've already used this Easy Link. No need to use this Easy Link again, you're already in."
            );
          } else if (err.code === 'MembershipCodeFull') {
            setError(true);
            setErrorMessage(
              'The Easy Link could not be used because it has been redeemed the maximum number of times.'
            );
          } else if (err.code === 'MembershipCodeExpired') {
            setError(true);
            setErrorMessage('This Easy Link could not be used because it has expired.');
          } else if (err.code === 'MembershipCodeIsInactive') {
            setError(true);
            setErrorMessage(
              'The Easy Link could not be used because it has been deactivated. Please contact the person who gave you this Easy Link.'
            );
          } else if (err.code === 'MembershipCodeDoesNotExist') {
            setError(true);
            setErrorMessage(
              "We couldn't recognize the Easy Link. Please try again and check for any mistyped characters."
            );
          } else if (typeof err === 'object' && err !== null && (err as { status?: number }).status === 404) {
            setError(true);
            setErrorMessage(
              "We couldn't recognize the Easy Link. Please try again and check for any mistyped characters."
            );
          } else {
            handleError(err);
          }
        });
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    redeem(codeInput);
  };

  const codeTooShort = submitted && (!codeInput || codeInput.replace(/\s|-/g, '').length < 8);
  const codeMissing = submitted && !codeInput;
  const hasInputError = error || codeTooShort || codeMissing;

  return (
    <RedeemInviteContainer>
      <CloseButton onClick={() => windowService.redirectTo('/dashboard')}>
        <i className="fas fa-times" />
      </CloseButton>
      <RedeemInviteFormContainer>
        <Form noValidate onSubmit={onSubmit}>
          <HeroFrame>
            <Logo src={getOriginBranding().logoSrc} alt={getOriginBranding().alt} />
            <div>
              <h4 style={{ margin: 0 }}>Redeem Your Easy Link</h4>
              <p>Enter your Easy Link here to get started.</p>
            </div>

            <EasyLinkInput
              type="text"
              placeholder="ABCD-WXYZ"
              value={codeInput}
              onChange={e => {
                setCodeInput(e.target.value.toUpperCase());
                if (error) {
                  setError(false);
                  setErrorMessage('');
                }
              }}
              maxLength={9}
              required
              className={hasInputError ? 'invalid' : ''}
            />
            {error && <RedeemInviteErrorMessage>{errorMessage}</RedeemInviteErrorMessage>}
            {!error && codeTooShort && (
              <RedeemInviteErrorMessage>Code must be at least 8 characters</RedeemInviteErrorMessage>
            )}

            {!error && codeMissing && <RedeemInviteErrorMessage>Code is required</RedeemInviteErrorMessage>}

            <PrimaryButton style={{ width: '100%' }} type="submit" disabled={preventDoubleRedeem}>
              {preventDoubleRedeem ? 'Redeeming' : 'Redeem'}
            </PrimaryButton>
          </HeroFrame>
        </Form>
      </RedeemInviteFormContainer>
    </RedeemInviteContainer>
  );
}
